import { ComboStatus, ComboType } from '@tcr-erp/shared';
import api from './api';
import { unwrapList, unwrapObject, type ListResult } from './api-envelope';
import type { PosComboDefinition } from '@/pages/POS/utils/comboMatcher';

export { ComboStatus, ComboType };

/**
 * Ba cách nhập giá combo ở form — chủ sở hữu ở đây vì đây là một phần contract
 * API (`ComboPayload.pricing_input_mode`). `comboPriceAllocation.ts` (tầng
 * pages/) import NGƯỢC LẠI từ đây — services/ không được phụ thuộc vào pages/.
 */
export type ComboPriceMode = 'combo_price' | 'percent' | 'manual';

export interface ComboComponent {
  product_id: string;
  variant_id: string;
  sku: string;
  product_name: string;
  qty: number;
  retail_price: number;
  /** Available sellable stock for this variant (not persisted). */
  available_qty?: number;
  /** Đơn giá của thành phần này trong combo (phân bổ từ combo_price/percent hoặc nhập tay). */
  price_in_combo?: number;
}

export interface ComboStaleComponent {
  sku: string;
  snapshot_retail_price: number;
  current_retail_price: number;
}

export interface ComboWarning {
  code: 'price_above_retail' | 'zero_price_line' | 'vat_share_shift';
  message: string;
  sku?: string;
  /**
   * Chỉ có ở code = "vat_share_shift" — dữ liệu thô (không format chuỗi) để
   * UI hiện badge %/ngưỡng màu, khớp `ComboPriceWarning`
   * (tcr-erp-be-tenant/src/modules/combo/combo-price-warnings.util.ts).
   */
  vat_rate?: number;
  declared_share_percent?: number;
  retail_share_percent?: number;
  shift_percent_points?: number;
}

export interface ComboRecord {
  _id: string;
  combo_name: string;
  combo_sku: string;
  combo_type: ComboType;
  description: string;
  component_items: ComboComponent[];
  combo_price: number;
  total_retail_price: number;
  combo_available_qty: number;
  /** Trạng thái hiển thị (đã soft-expire theo valid_to). */
  status: ComboStatus;
  /** Status trong DB khi khác status hiển thị — dùng cho action lifecycle. */
  status_stored?: ComboStatus;
  valid_from?: string;
  valid_to?: string;
  created_at?: string;
  created_by?: string;
  created_by_name?: string;
  /** true khi giá lẻ hiện tại của một hoặc nhiều thành phần đã đổi so với lúc chốt giá combo. */
  retail_price_stale?: boolean;
  /** Chi tiết các thành phần có giá lẻ lệch snapshot — chỉ có ở GET detail. */
  stale_components?: ComboStaleComponent[];
  /** Cảnh báo giá dòng từ BE — chỉ có ở response create/update. */
  warnings?: ComboWarning[];
}

export interface ComboPayload {
  combo_name: string;
  /** Để trống / omit → BE tự sinh CB-000001. */
  combo_sku?: string;
  combo_type: ComboType;
  description?: string;
  component_items: ComboComponent[];
  /**
   * BE bỏ qua khi component_items đã có đủ price_in_combo — giữ optional để
   * tương thích payload cũ.
   *
   * FIELD CHUYỂN TIẾP — XOÁ Ở PR DỌN DẸP (spec §8.2 bước 5): `combo_price` là
   * số DẪN XUẤT (`Σ price_in_combo × qty`), client không có việc gì phải gửi nó.
   */
  combo_price?: number;
  /** Cách người dùng nhập giá ở form — BE dùng để diễn giải component_items[].price_in_combo. */
  pricing_input_mode?: ComboPriceMode;
  /** % giảm khi pricing_input_mode = 'percent'. */
  discount_percent?: number;
  valid_from?: string;
  valid_to?: string;
  /** true = tạo/lưu trực tiếp ACTIVE. */
  activate?: boolean;
}

export interface ComboStockWarehouseRow {
  warehouse_id: string;
  warehouse_name: string;
  warehouse_code: string;
  combo_available_qty: number;
}

export interface ComboStockStoreRow {
  store_id: string;
  store_name: string;
  store_code: string;
  combo_available_qty: number;
  warehouses: ComboStockWarehouseRow[];
}

export interface ComboStockByLocation {
  tenant_combo_available_qty: number;
  by_store: ComboStockStoreRow[];
}

export const comboService = {
  list: (params: {
    page?: number;
    limit?: number;
    search?: string;
    combo_type?: ComboType;
    status?: ComboStatus;
    date_from?: string;
    date_to?: string;
  }): Promise<ListResult<ComboRecord>> =>
    api.get('/combos', { params }).then(unwrapList<ComboRecord>),
  detail: (id: string): Promise<ComboRecord> => api.get(`/combos/${id}`).then(unwrapObject<ComboRecord>),
  create: async (payload: ComboPayload): Promise<ComboRecord> => {
    const res = await api.post('/combos', payload);
    // HTTP 2xx = tạo thành công — không chặn navigate vì envelope lệch.
    const body = res.data as { data?: ComboRecord } | ComboRecord | undefined;
    if (body && typeof body === 'object' && 'data' in body && body.data) {
      return body.data;
    }
    return (body ?? {}) as ComboRecord;
  },
  update: (id: string, payload: ComboPayload): Promise<ComboRecord> => api.put(`/combos/${id}`, payload).then(unwrapObject<ComboRecord>),
  activate: (id: string): Promise<ComboRecord> => api.patch(`/combos/${id}/activate`).then(unwrapObject<ComboRecord>),
  pause: (id: string): Promise<ComboRecord> => api.patch(`/combos/${id}/pause`).then(unwrapObject<ComboRecord>),
  archive: (id: string): Promise<ComboRecord> => api.patch(`/combos/${id}/archive`).then(unwrapObject<ComboRecord>),
  componentStock: (variantIds: string[]): Promise<Array<{ variant_id: string; available_qty: number }>> => {
    if (!variantIds.length) return Promise.resolve([]);
    return api
      .get('/combos/component-stock', { params: { variant_ids: variantIds.join(',') } })
      .then((res) => {
        const payload = res.data;
        return (payload?.data ?? []) as Array<{ variant_id: string; available_qty: number }>;
      });
  },
  /** Tồn combo theo chi nhánh + kho (cùng công thức POS). */
  stockByLocation: (id: string): Promise<ComboStockByLocation> =>
    api.get(`/combos/${id}/stock-by-location`).then(unwrapObject<ComboStockByLocation>),
  /** Preview tồn theo chi nhánh + kho cho danh sách thành phần (chưa có combo ID). */
  previewStockByLocation: (items: Array<{ variant_id: string; qty: number }>): Promise<ComboStockByLocation> =>
    api.post('/combos/preview-stock-by-location', { component_items: items }).then(unwrapObject<ComboStockByLocation>),
  /** Combo ảo dùng được ngay tại POS — đã lọc ACTIVE + trong hiệu lực ở BE. */
  posActive: (): Promise<PosComboDefinition[]> =>
    api.get('/combos/pos-active').then((res) => (res.data?.data ?? []) as PosComboDefinition[]),
};

/** Làm mới snapshot giá lẻ của các thành phần — KHÔNG đổi giá combo. */
export async function refreshComboRetailPrice(id: string): Promise<ComboRecord> {
  return api.post(`/combos/${id}/refresh-retail-price`).then(unwrapObject<ComboRecord>);
}
